/**
 * The small rules the deal link, the results log and the Bearri link forms
 * share — formatting and validation only, no React. Validation here mirrors
 * the server's limits so a person hears about a problem before sending; the
 * server is still the judge and its sentence is shown when it refuses.
 */
import { toast } from 'sonner';
import type { AllowBearriLinkAnswer, DealResultInput, ShareResultsAnswer } from '../../services/dealsService';

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const NAME_MAX = 80;
export const EMAIL_MAX = 120;
export const MESSAGE_MAX = 2000;
export const COMMENT_MAX = 1000;
export const NOTES_MAX = 1000;
export const LABEL_MAX = 80;
const NUMBER_MAX = 10_000_000;

/** "2026-09-01" → "Sep 1, 2026". A calendar day, read as a calendar day (no timezone shift). */
export function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatPeriod(start: string, end: string): string {
  return start === end ? formatDay(start) : `${formatDay(start)} – ${formatDay(end)}`;
}

/** An ISO timestamp → "Sep 30, 2026". '' for nothing usable. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const isPast = (iso: string | null | undefined): boolean => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t <= Date.now();
};

export const SHARE_RESULTS_LABEL: Record<ShareResultsAnswer, string> = { yes: 'Yes', not_now: 'Not now' };
export const ALLOW_LINK_LABEL: Record<AllowBearriLinkAnswer, string> = { yes: 'Yes', no: 'No' };

/** Asked before hiding a reply or a row of results. Unhiding needs no question — nothing is lost either way. */
export const HIDE_CONFIRM = 'Hide this? It stays in your records but won’t show here.';

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

export async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copied.');
  } catch {
    toast.error('Could not copy — select the link and copy it by hand.');
  }
}

/* ── the results form (the creator's own numbers, and the brand's report) ── */

export interface ResultsDraft {
  period_start: string;
  period_end: string;
  code_redemptions: string;
  orders: string;
  clicks: string;
  notes: string;
}

export const EMPTY_RESULTS_DRAFT: ResultsDraft = {
  period_start: '', period_end: '', code_redemptions: '', orders: '', clicks: '', notes: '',
};

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER_FIELDS: Array<['code_redemptions' | 'orders' | 'clicks', string]> = [
  ['code_redemptions', 'Code redemptions'],
  ['orders', 'Orders'],
  ['clicks', 'Clicks'],
];

/**
 * A draft → the request body, or the one sentence that says what to fix.
 * A blank number is left out (not reported), never sent as 0.
 */
export function parseResultsDraft(draft: ResultsDraft): { input: DealResultInput; error: null } | { input: null; error: string } {
  const start = draft.period_start.trim();
  const end = draft.period_end.trim();
  if (!DAY_RE.test(start) || !DAY_RE.test(end)) return { input: null, error: 'Pick the first and last day these numbers cover.' };
  if (start > end) return { input: null, error: 'The period ends before it starts.' };
  const input: DealResultInput = { period_start: start, period_end: end };
  let any = false;
  for (const [key, label] of NUMBER_FIELDS) {
    const raw = draft[key].trim().replace(/,/g, '');
    if (!raw) continue;
    if (!/^\d+$/.test(raw)) return { input: null, error: `${label} must be a whole number (0 or more).` };
    const n = Number(raw);
    if (n > NUMBER_MAX) return { input: null, error: `${label} looks too large — check the number.` };
    input[key] = n;
    any = true;
  }
  if (!any) return { input: null, error: 'Add at least one number — code redemptions, orders or clicks.' };
  const notes = draft.notes.trim();
  if (notes.length > NOTES_MAX) return { input: null, error: `Notes can be ${NOTES_MAX.toLocaleString('en-US')} characters at most.` };
  if (notes) input.notes = notes;
  return { input, error: null };
}

/** Who is replying on a deal link: required, and stored exactly as typed. */
export function identityProblem(name: string, email: string): string | null {
  const n = name.trim();
  const e = email.trim();
  if (!n) return 'Add your name so the creator knows who replied.';
  if (n.length > NAME_MAX) return `Your name can be ${NAME_MAX} characters at most.`;
  if (!e) return 'Add your work email so the creator can reach you.';
  if (e.length > EMAIL_MAX || !EMAIL_RE.test(e)) return 'That email doesn’t look right — check it and try again.';
  return null;
}

/** One section's data on the page: still coming, here, or failed with a sentence. */
export type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: string };
